import React from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

import { cn } from '../../utils/cn';

const SME_JOURNEY_STEPS = [
  { key: 'queue', label: 'Work queue', hint: 'Assessments awaiting review' },
  { key: 'review', label: 'Review', hint: 'Client responses & ratings' },
  { key: 'chat', label: 'Chat', hint: 'Collaborate with the client' },
  { key: 'report', label: 'Report', hint: 'Publish your evaluation' },
] as const;

function firstName(fullName?: string): string {
  const trimmed = (fullName || '').trim();
  if (!trimmed) return 'there';
  return trimmed.split(/\s+/)[0] || 'there';
}

export type SmeDashboardHeroProps = {
  userName?: string;
  pendingCount: number;
};

export function SmeDashboardHero({ userName, pendingCount }: SmeDashboardHeroProps) {
  const greeting = firstName(userName);
  const journeyIndex = pendingCount > 0 ? 0 : 1;

  const subtitle =
    pendingCount > 0
      ? `You have ${pendingCount.toLocaleString()} assessment${pendingCount === 1 ? '' : 's'} in your queue. Open Assessment Tracking to review, chat, and publish reports.`
      : 'When clients submit assessments assigned to you, they appear in your work queue and in Assessment Tracking.';

  return (
    <header className="client-dash__hero app-welcome-card sme-dash__hero">
      <div className="app-welcome-card-glow client-dash__hero-glow" aria-hidden />
      <div className="client-dash__hero-inner">
        <div className="client-dash__hero-copy">
          <p className="client-hero-eyebrow client-dash__eyebrow">SME workspace</p>
          <h1 className="client-hero-title app-heading-accent client-dash__title">
            Welcome back, {greeting}
          </h1>
          <p className="client-hero-desc client-dash__subtitle">{subtitle}</p>
          <div className="client-dash__chips">
            <span className="app-welcome-card-chip">
              {pendingCount > 0 ? `${pendingCount} under review` : 'Queue clear'}
            </span>
            <Link to="/sme/assesstracking" className="app-welcome-card-chip sme-dash__chip-link">
              Assessment Tracking →
            </Link>
          </div>
        </div>

        <div className="client-dash__journey" aria-label="SME workflow">
          <p className="client-dash__journey-label">Your workflow</p>
          <ol className="client-dash__journey-steps">
            {SME_JOURNEY_STEPS.map((step, index) => {
              const done = index < journeyIndex;
              const active = index === journeyIndex;
              return (
                <li
                  key={step.key}
                  className={cn(
                    'client-dash__journey-step',
                    done && 'is-done',
                    active && 'is-active'
                  )}
                >
                  <span className="client-dash__journey-marker" aria-hidden>
                    {done ? <Check size={12} strokeWidth={2.5} /> : index + 1}
                  </span>
                  <span className="client-dash__journey-text">
                    <span className="client-dash__journey-name">{step.label}</span>
                    <span className="client-dash__journey-hint">{step.hint}</span>
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </header>
  );
}

export default SmeDashboardHero;
